import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { Wrench, Home, Phone, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/ui-custom/SEO';

const NotFound = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.error-card',
        { opacity: 0, scale: 0.95, y: 20 },
        { opacity: 1, scale: 1, y: 0, duration: 0.5, ease: 'power2.out' }
      );
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <>
      <SEO 
        title="Page Not Found | Ard's Plumbing & Electric" 
        description="The page you are looking for might have been moved or deleted. Return to Ard's Plumbing & Electric home page."
      />
      
      <section 
        ref={containerRef}
        className="min-h-screen bg-brand-blue flex items-center justify-center pt-20 pb-12 px-4"
      >
        <div className="error-card w-full max-w-2xl bg-brand-white rounded-[28px] overflow-hidden shadow-2xl p-8 lg:p-12 text-center">
          <div className="w-20 h-20 bg-brand-coral/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <AlertTriangle className="w-10 h-10 text-brand-coral" />
          </div>

          <h1 className="font-heading font-black text-4xl lg:text-6xl text-brand-black mb-4">
            404
          </h1>
          <h2 className="font-heading font-bold text-2xl text-brand-navy mb-6">
            OOPS! SOMETHING IS LEAKING.
          </h2>
          
          <p className="text-lg text-gray-600 mb-10 max-w-md mx-auto">
            We couldn't find the page you're looking for. It might have been moved, 
            deleted, or never existed in the first place.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-brand-navy hover:bg-brand-navy/90 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-md">
              <Link to="/" className="flex items-center gap-2">
                <Home className="w-5 h-5" /> Back to Home
              </Link>
            </Button>
            
            <a 
              href="tel:3346925537" 
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border-2 border-brand-navy text-brand-navy font-semibold hover:bg-brand-navy hover:text-white transition-colors"
            >
              <Phone className="w-5 h-5" /> Support
            </a>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-100 flex items-center justify-center gap-2 text-gray-400">
            <Wrench className="w-4 h-4" />
            <span className="text-sm font-medium uppercase tracking-widest">Ard's Plumbing & Electric</span>
          </div>
        </div>
      </section>
    </>
  );
};

export default NotFound;